import { useEffect, useMemo, useState } from "react";
import { Button, Form, Modal, Table } from "react-bootstrap";
import { FiCheck, FiChevronDown, FiChevronRight, FiX } from "react-icons/fi";
import { toast } from "react-toastify";
import axiosInstance from "../../axiosInstance";
import "../../css/ChildUserPermissionEditor.css";

// Dựng cây cha/con từ danh sách rows phẳng dựa trên `level` (0 = cấp cao nhất). Một row là con của
// row gần nhất phía trước có level nhỏ hơn - đúng với cách backend phát sinh rows tuần tự trong
// ChildUserFeature.defaultPermissionRows() (feature cha luôn đứng ngay trước các cột con của nó).
const buildHierarchy = (rows) => {
  const parentIndex = new Array(rows.length).fill(-1);
  const childrenOf = rows.map(() => []);
  const stack = [];
  rows.forEach((row, i) => {
    const level = row.level ?? 0;
    while (stack.length && stack[stack.length - 1].level >= level) {
      stack.pop();
    }
    if (stack.length) {
      const parent = stack[stack.length - 1].index;
      parentIndex[i] = parent;
      childrenOf[parent].push(i);
    }
    stack.push({ index: i, level });
  });
  return { parentIndex, childrenOf };
};

const ChildUserPermissionEditor = ({
  show,
  user,
  onClose,
  onSaved,
  variant = "modal",
  parentUserId = null,
}) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  // Mặc định thu gọn mọi nhóm có cột con - bảng đỡ dài, admin bấm mở rộng khi cần cấu hình cột.
  const [collapsedIndices, setCollapsedIndices] = useState(() => new Set());
  const isPanel = variant === "panel";
  const isActive = isPanel ? Boolean(user?.id) : show;

  useEffect(() => {
    if (!isActive || !user?.id) {
      if (isPanel) {
        setRows([]);
        setCollapsedIndices(new Set());
      }
      return;
    }
    let cancelled = false;
    const loadPermissions = async () => {
      setLoading(true);
      try {
        const response = await axiosInstance.get("/api/users/childUserPermissions", {
          params: {
            userId: user.id,
            ...(parentUserId ? { parentUserId } : {}),
          },
        });
        if (cancelled) return;
        if (!response.data?.success) {
          toast.error(response.data?.message || "Không tải được phân quyền.");
          setRows([]);
          return;
        }
        const loadedRows = response.data.data || [];
        setRows(loadedRows);
        // Mặc định thu gọn tất cả các nhóm có con (feature cấp trang + nhóm "Đối soát").
        const { childrenOf } = buildHierarchy(loadedRows);
        const withChildren = childrenOf.reduce((acc, children, i) => {
          if (children.length > 0) acc.add(i);
          return acc;
        }, new Set());
        setCollapsedIndices(withChildren);
      } catch (err) {
        if (!cancelled) {
          toast.error(err.message || "Không tải được phân quyền.");
          setRows([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };
    loadPermissions();
    return () => {
      cancelled = true;
    };
  }, [isActive, isPanel, user?.id, parentUserId]);

  const { parentIndex, childrenOf } = useMemo(() => buildHierarchy(rows), [rows]);

  const isVisible = (i) => {
    let p = parentIndex[i];
    while (p !== -1) {
      if (collapsedIndices.has(p)) return false;
      p = parentIndex[p];
    }
    return true;
  };

  const collapsibleIndices = useMemo(
    () => childrenOf.reduce((acc, children, i) => {
      if (children.length > 0) acc.push(i);
      return acc;
    }, []),
    [childrenOf]
  );
  const allExpanded =
    collapsibleIndices.length > 0 && collapsibleIndices.every((i) => !collapsedIndices.has(i));

  const handleToggleAll = () => {
    setCollapsedIndices(allExpanded ? new Set(collapsibleIndices) : new Set());
  };

  const toggleCollapse = (i) => {
    setCollapsedIndices((prev) => {
      const next = new Set(prev);
      if (next.has(i)) {
        next.delete(i);
      } else {
        next.add(i);
      }
      return next;
    });
  };

  const handleToggle = (feature, checked) => {
    setRows((prev) =>
      prev.map((row) =>
        row.feature === feature ? { ...row, view: checked } : row
      )
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user?.id) return;
    setSaving(true);
    try {
      const permissions = rows
        .filter((row) => row.feature && !row.groupHeader)
        .map((row) => ({
          feature: row.feature,
          view: !!row.view,
        }));
      const response = await axiosInstance.post("/api/users/saveChildUserPermissions", {
        userId: user.id,
        permissions,
        ...(parentUserId ? { parentUserId } : {}),
      });
      if (!response.data?.success) {
        toast.error(response.data?.message || "Không lưu được phân quyền.");
        return;
      }
      toast.success(response.data.message || "Lưu phân quyền thành công.");
      onSaved?.();
      if (!isPanel) {
        onClose?.();
      }
    } catch (err) {
      toast.error(err.message || "Không lưu được phân quyền.");
    } finally {
      setSaving(false);
    }
  };

  let stt = 0;

  const permissionTable = (
    <>
      {loading ? (
        <div className="text-center py-4">Đang tải…</div>
      ) : (
        <div className="child-user-perm-wrap border">
          {collapsibleIndices.length > 0 && (
            <div className="child-user-perm-toolbar d-flex justify-content-end px-2 py-1 border-bottom bg-light">
              <button
                type="button"
                className="btn btn-sm btn-link p-0 d-inline-flex align-items-center gap-1"
                onClick={handleToggleAll}
              >
                {allExpanded ? <FiChevronDown /> : <FiChevronRight />}
                {allExpanded ? "Thu gọn tất cả" : "Mở rộng tất cả cột"}
              </button>
            </div>
          )}
          {/* Vùng scroll riêng, có chiều cao giới hạn - không phụ thuộc ancestor flex (trang
              khách hàng UserLayout không có khung viewport cố định như AdLayout), tránh việc
              cả trang bị kéo dài/scroll khi danh sách quyền dài ra do thêm quyền cấp cột. */}
          <div className="table-responsive child-user-perm-scroll">
            <Table bordered hover size="sm" className="mb-0 child-user-perm-table">
              <thead className="table-primary">
                <tr>
                  <th width="60" className="text-center">
                    STT
                  </th>
                  <th className="perm-name-col">Tên chức năng</th>
                  <th width="90" className="text-center">
                    Xem
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => {
                if (!isVisible(i)) return null;

                const canExpand = childrenOf[i].length > 0;
                const isCollapsed = collapsedIndices.has(i);
                const level = row.level ?? 0;
                const isParent = level === 0;
                if (isParent) stt += 1;
                const sttCell = isParent ? stt : "";

                if (row.groupHeader) {
                  return (
                    <tr key={`group-${i}`} className="child-user-perm-group-row child-user-perm-parent-row">
                      <td className="text-center fw-semibold">{sttCell}</td>
                      <td className="fw-semibold text-start perm-name-cell perm-name-cell--parent">
                        {canExpand && (
                          <button
                            type="button"
                            className="btn btn-sm btn-link p-0 me-1 child-user-perm-expand-btn"
                            onClick={() => toggleCollapse(i)}
                            aria-label={isCollapsed ? `Mở rộng ${row.label}` : `Thu gọn ${row.label}`}
                          >
                            {isCollapsed ? <FiChevronRight /> : <FiChevronDown />}
                          </button>
                        )}
                        {row.label}
                      </td>
                      <td />
                    </tr>
                  );
                }
                const nameCellClass = [
                  "text-start",
                  "perm-name-cell",
                  isParent ? "perm-name-cell--parent" : "",
                  level >= 1 ? `perm-name-cell--level-${Math.min(level, 2)}` : "",
                  level >= 1 ? "child-user-perm-column-row" : "",
                ]
                  .filter(Boolean)
                  .join(" ");
                const rowClass = [
                  isParent ? "child-user-perm-parent-row" : "",
                  level >= 1 ? "child-user-perm-column-row" : "",
                  level >= 2 ? "child-user-perm-leaf-row" : "",
                ]
                  .filter(Boolean)
                  .join(" ");
                return (
                  <tr key={row.feature} className={rowClass}>
                    <td className={`text-center ${isParent ? "fw-semibold" : ""}`}>{sttCell}</td>
                    <td className={nameCellClass}>
                      {canExpand && (
                        <button
                          type="button"
                          className="btn btn-sm btn-link p-0 me-1 child-user-perm-expand-btn"
                          onClick={() => toggleCollapse(i)}
                          aria-label={isCollapsed ? `Mở rộng ${row.label}` : `Thu gọn ${row.label}`}
                        >
                          {isCollapsed ? <FiChevronRight /> : <FiChevronDown />}
                        </button>
                      )}
                      {row.label}
                    </td>
                    <td className="text-center">
                      <Form.Check
                        type="checkbox"
                        checked={!!row.view}
                        onChange={(e) => handleToggle(row.feature, e.target.checked)}
                        aria-label={`Xem ${row.label}`}
                      />
                    </td>
                  </tr>
                );
              })}
                {!loading && rows.length === 0 && (
                  <tr>
                    <td colSpan="3" className="text-center">
                      Không có dữ liệu phân quyền
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </div>
      )}
    </>
  );

  const saveButton = (
    <Button size="sm" variant="primary" type="submit" disabled={saving || loading}>
      <FiCheck className="me-1" />
      {saving ? "Đang lưu…" : "Lưu"}
    </Button>
  );

  if (isPanel) {
    const panelTitle = user?.id
      ? `Thông tin phân quyền: ${user.name || user.username || user.email || "—"}`
      : "Thông tin phân quyền";

    return (
      <div className="card h-100 d-flex flex-column enterprise-permission-editor-panel">
        <div className="card-header fw-semibold py-3">{panelTitle}</div>
        <Form onSubmit={handleSubmit} className="d-flex flex-column flex-grow-1 min-h-0">
          <div className="card-body">
            {!user?.id ? (
              <div className="text-muted">Vui lòng chọn một user bên trái.</div>
            ) : (
              permissionTable
            )}
          </div>
          {user?.id && (
            <div className="card-footer d-flex justify-content-end mt-auto border-top bg-white py-3">
              {saveButton}
            </div>
          )}
        </Form>
      </div>
    );
  }

  return (
    <Modal show={show} onHide={onClose} centered backdrop="static" size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          Phân quyền{user?.name ? `: ${user.name}` : ""}
        </Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>{permissionTable}</Modal.Body>
        <Modal.Footer>
          <Button size="sm" variant="secondary" onClick={onClose} disabled={saving}>
            <FiX className="me-1" />
            Đóng
          </Button>
          {saveButton}
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default ChildUserPermissionEditor;
